/**
 * Formula Editor Component
 * 
 * Allows creating calculated variables using formulas
 * that reference other numeric variables.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Calculator,
  Variable as VariableIcon,
  AlertCircle,
  Check,
  ChevronDown
} from 'lucide-react';

interface AvailableVariable {
  name: string;
  label: string;
  valueType: string;
  blockName?: string;
}

interface FormulaEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (variable: { name: string; label: string; formula: string; helpText?: string }) => void;
  availableVariables: AvailableVariable[];
  initialData?: {
    name: string;
    label: string;
    formula: string;
    helpText?: string;
  };
}

export default function FormulaEditor({
  isOpen,
  onClose,
  onSave,
  availableVariables,
  initialData
}: FormulaEditorProps) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [formula, setFormula] = useState('');
  const [helpText, setHelpText] = useState('');
  const [error, setError] = useState('');
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);

  // Filter only numeric variables
  const numericVariables = availableVariables.filter(v => 
    ['NUMBER', 'CURRENCY', 'FORMULA'].includes(v.valueType)
  );

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setLabel(initialData.label);
      setFormula(initialData.formula);
      setHelpText(initialData.helpText || '');
    } else {
      setName('');
      setLabel('');
      setFormula('');
      setHelpText('');
    }
    setError('');
  }, [initialData, isOpen]);

  // Insert variable into formula
  const insertVariable = (varName: string) => {
    setFormula(prev => prev + `<<${varName}>>`);
    setShowVariableDropdown(false);
  };

  // Insert operator
  const insertOperator = (op: string) => {
    setFormula(prev => prev + ` ${op} `);
  };

  // Validate formula
  const validateFormula = (): boolean => {
    if (!name.trim()) {
      setError('Variablenname ist erforderlich');
      return false;
    }
    if (!label.trim()) {
      setError('Bezeichnung ist erforderlich');
      return false;
    }
    if (!formula.trim()) {
      setError('Formel ist erforderlich');
      return false;
    }

    // Check if formula contains at least one variable reference
    const varPattern = /<<([^<>]+)>>/g;
    const matches = formula.match(varPattern);
    if (!matches || matches.length === 0) {
      setError('Formel muss mindestens eine Variable enthalten');
      return false;
    }

    // Check if all referenced variables exist
    const referencedVars = matches.map(m => m.replace(/<<|>>/g, ''));
    const availableVarNames = availableVariables.map(v => v.name);
    const missingVars = referencedVars.filter(v => !availableVarNames.includes(v));
    if (missingVars.length > 0) {
      setError(`Unbekannte Variable(n): ${missingVars.join(', ')}`);
      return false;
    }

    // Conservative client-side validation; authoritative parsing happens server-side.
    const testFormula = formula.replace(/<<[^<>]+>>/g, '1');
    if (!/^[\d\s+\-*/().,]+$/.test(testFormula) || (testFormula.match(/\(/g)?.length || 0) !== (testFormula.match(/\)/g)?.length || 0)) {
      setError('Ungültige Formel-Syntax');
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!validateFormula()) return;

    onSave({
      name: name.trim(),
      label: label.trim(),
      formula: formula.trim(),
      helpText: helpText.trim() || undefined
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <Calculator className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? 'Formel bearbeiten' : 'Neue Formel-Variable'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Variable Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Variablenname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              placeholder="z.B. gesamtmiete"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Nur Buchstaben, Zahlen und Unterstriche. Wird als {"<<name>>"} verwendet.
            </p>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezeichnung <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Gesamtmiete"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Formula */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Formel <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <textarea
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder="z.B. <<hauptmietzins>> + <<betriebskosten>>"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>

            {/* Operators */}
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-xs text-gray-500 mr-2">Operatoren:</span>
              {['+', '-', '*', '/', '(', ')'].map(op => (
                <button
                  key={op}
                  type="button"
                  onClick={() => insertOperator(op)}
                  className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm font-mono"
                >
                  {op}
                </button>
              ))}
            </div>

            {/* Variable Dropdown */}
            <div className="mt-2 relative">
              <button
                type="button"
                onClick={() => setShowVariableDropdown(!showVariableDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 text-sm"
              >
                <VariableIcon className="h-4 w-4" />
                Variable einfügen
                <ChevronDown className={`h-4 w-4 transition-transform ${showVariableDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showVariableDropdown && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {numericVariables.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">
                      Keine numerischen Variablen verfügbar
                    </div>
                  ) : (
                    numericVariables.map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => insertVariable(v.name)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between group"
                      >
                        <div>
                          <span className="font-mono text-sm text-primary-600">
                            {"<<" + v.name + ">>"}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">{v.label}</span>
                        </div>
                        <span className="text-xs text-gray-400 group-hover:text-gray-600">
                          {v.valueType}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Help Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hilfetext (optional)
            </label>
            <input
              type="text"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              placeholder="z.B. Berechnet aus Hauptmietzins + Betriebskosten"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Preview */}
          {formula && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Vorschau:</p>
              <p className="font-mono text-sm">
                {label || 'Variable'} = {formula}
              </p>
            </div>
          )}

          {/* Info */}
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <p className="font-medium mb-1">Hinweis:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Verwenden Sie {"<<variablenname>>"} um auf andere Variablen zu verweisen</li>
              <li>Nur numerische Variablen (Zahl, Währung, Formel) können verwendet werden</li>
              <li>Verfügbare Operatoren: + (Addition), - (Subtraktion), * (Multiplikation), / (Division)</li>
              <li>Klammern für Gruppierung: (a + b) * c</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Check className="h-4 w-4" />
            {initialData ? 'Speichern' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
